/**
 * ClientLookupField — the ONE client picker for the writing tools.
 *
 * Lookup-first with a free-text fallback owned by the caller (both paths
 * are real: intake precedes the client record). The lookup searches every
 * client the viewer may open: GET /v1/clients, which answers with every
 * client or the viewer's engagement lane, so callers pass
 * canViewAnyClientRoster as canLookup. Coverage and emergencies need any
 * of them, not only the viewer's own; the viewer's own clients (the
 * caseload, when given) come first. A viewer with no roster at all looks up
 * from the caseload alone. The search is lazy (the list loads once the
 * person starts typing) and the result list shows up to eight active
 * clients whose name contains the typed text.
 *
 * Presentational: the caller decides what a pick means (fill name fields,
 * link a draft, pull allergies). testIdPrefix keeps each caller's test
 * hooks distinct: `${prefix}-lookup`, `${prefix}-lookup-input`,
 * `${prefix}-lookup-results`, `${prefix}-option-${id}`.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { useClientsList, type ClientListRowWire } from '@/lib/clientsApi';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';

const LABEL_CLS = 'block text-title-sm font-semibold text-sage-800';

export type ClientLookupPick = Pick<ClientListRowWire, 'id' | 'display_name' | 'pronouns'>;

export interface ClientLookupFieldProps {
  tenantSlug: string;
  /** canViewAnyClientRoster (GET /v1/clients answers with every client or
   *  the viewer's lane); false = render nothing unless a caseload is given. */
  canLookup: boolean;
  /** The viewer's own clients (the rows the home read carries): first in
   *  the results, and the whole source when canLookup is false. */
  caseload?: ReadonlyArray<ClientLookupPick>;
  onPick: (client: ClientLookupPick) => void;
  disabled?: boolean;
  /** The input's id (for the label) and the test-hook prefix. */
  inputId?: string;
  testIdPrefix?: string;
  label?: string;
  hint?: string;
  placeholder?: string;
  className?: string;
}

/** The viewer's own clients first, then every other row, each in its
 *  given order. An own client the roster read did not return is still
 *  listed: it is hers to open. */
export function ownClientsFirst<T extends { id: string }>(rows: ReadonlyArray<T>, own: ReadonlyArray<T> | undefined): T[] {
  if (!own || own.length === 0) return [...rows];
  const ids = new Set(own.map((c) => c.id));
  return [...own, ...rows.filter((r) => !ids.has(r.id))];
}

export function ClientLookupField({
  tenantSlug,
  canLookup,
  caseload,
  onPick,
  disabled = false,
  inputId = 'client-lookup',
  testIdPrefix = 'client',
  label = 'Find a client',
  hint,
  placeholder = 'Find a client',
  className = 'relative mt-3',
}: ClientLookupFieldProps): ReactNode {
  // Lookup-first: the search fires only after the CM starts typing in the
  // lookup field (lazy; a denied role never sees the field at all).
  const [lookupText, setLookupText] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const usesCaseload = !canLookup && caseload !== undefined;
  const { clients, isError } = useClientsList(tenantSlug, {
    enabled: canLookup && lookupOpen,
  });

  const matches = useMemo<ClientLookupPick[]>(() => {
    const q = lookupText.trim().toLowerCase();
    if (q.length === 0) return [];
    const source: ReadonlyArray<ClientLookupPick> = usesCaseload
      ? caseload
      : ownClientsFirst<ClientLookupPick>(clients.filter((c) => c.is_active), caseload);
    return source.filter((c) => c.display_name.toLowerCase().includes(q)).slice(0, 8);
  }, [clients, caseload, usesCaseload, lookupText]);

  if (!(canLookup || usesCaseload) || isError) return null;

  const pick = (c: ClientLookupPick): void => {
    onPick(c);
    setLookupText('');
    setLookupOpen(false);
  };

  return (
    <div className={className} data-testid={`${testIdPrefix}-lookup`}>
      <label htmlFor={inputId} className={LABEL_CLS}>
        {label}
      </label>
      {hint && <p className="text-xs text-ink-soft mt-0.5">{hint}</p>}
      <SearchField
        id={inputId}
        value={lookupText}
        onChange={(next) => {
          setLookupText(next);
          setLookupOpen(true);
        }}
        onFocus={() => setLookupOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        testId={`${testIdPrefix}-lookup-input`}
        className="mt-1.5"
      />
      {lookupOpen && matches.length > 0 && (
        <ul
          className="absolute z-10 mt-1 w-full overflow-hidden rounded-[12px] border border-sage-400/25 bg-paper py-1 shadow-sh2"
          data-testid={`${testIdPrefix}-lookup-results`}
        >
          {matches.map((c) => (
            <li key={c.id}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full !justify-start"
                onClick={() => pick(c)}
                data-testid={`${testIdPrefix}-option-${c.id}`}
              >
                {c.display_name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
